/**
 * Department Management routes (admin side, under MainLayout with the departments menu).
 *
 *  /app/departments                      Dashboard
 *  /app/departments/list                 Departments
 *  /app/departments/view/:id             Department detail (?tab=overview|members|designations)
 *  /app/departments/designations         Every designation
 *  /app/departments/members              Every department membership
 *  /app/departments/ministries           Ministries
 *  /app/departments/ministries/members   Every ministry membership
 *  /app/departments/ministries/teams     Teams across ministries
 *  /app/departments/ministries/equipment Equipment across ministries
 *  /app/departments/ministries/activities Activities across ministries
 *  /app/departments/ministries/:id       Ministry detail (?tab=members|teams|equipment|activities)
 *  /app/departments/settings/ministry-roles  Ministry role lookup
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';

// Eager load dashboard for instant access
import DepartmentsDashboard from './dashboard';

const DepartmentListPage = lazy(() => import('./list'));
const DepartmentDetailPage = lazy(() => import('./view/[id]'));
const DesignationsPage = lazy(() => import('./designations'));
const DepartmentMembersPage = lazy(() => import('./members'));
const MinistryListPage = lazy(() => import('./ministries'));
const MinistryDetailPage = lazy(() => import('./ministries/[id]'));
const MinistryMembersPage = lazy(() => import('./ministries/members'));
const MinistryTeamsPage = lazy(() => import('./ministries/teams'));
const MinistryEquipmentPage = lazy(() => import('./ministries/equipment'));
const MinistryActivitiesPage = lazy(() => import('./ministries/activities'));
const MinistryRolesPage = lazy(() => import('./settings/ministry-roles'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

export default function DepartmentManagementRoutes() {
  return (
    <MainLayout domainKey="departments">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<DepartmentsDashboard />} />

          <Route path="/list" element={<DepartmentListPage />} />
          <Route path="/view/:id" element={<DepartmentDetailPage />} />
          <Route path="/designations" element={<DesignationsPage />} />
          <Route path="/members" element={<DepartmentMembersPage />} />

          {/* Static ministry pages must come before the :id route. */}
          <Route path="/ministries" element={<MinistryListPage />} />
          <Route path="/ministries/members" element={<MinistryMembersPage />} />
          <Route path="/ministries/teams" element={<MinistryTeamsPage />} />
          <Route path="/ministries/equipment" element={<MinistryEquipmentPage />} />
          <Route path="/ministries/activities" element={<MinistryActivitiesPage />} />
          <Route path="/ministries/:id" element={<MinistryDetailPage />} />

          <Route path="/settings/ministry-roles" element={<MinistryRolesPage />} />

          <Route path="*" element={<Navigate to="/app/departments" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
